import { useEffect, useMemo, useState } from 'react';
import type { SpellRef } from '@/schemas/character';
import { isAutoSpellGrantLevelRule } from '@/schemas/move';
import type { Spell } from '@/schemas/spell';
import {
  type ActiveSpellGrant,
  getDisplayableSpellGrantsForLevelUpMany,
  getGrantKey,
} from '@/services/spellGrants';
import {
  findChiericoLineActiveList,
  getSpellPoolForGrantInContext,
  type SpellPoolContext,
} from '@/services/spellPoolDisplay';
import { type ActiveSpellList } from '@/services/spellAccess';
import { expandStartingMoveRefsWithBundles } from '@/services/bundle';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';
import { SpellPickPanel } from './SpellPickPanel';

/**
 * Step 9 — Incantesimi.
 *
 * **Sezione SEMPRE visibile**. Il contenuto è derivato dalle **grant** che il
 * PG ha attive — `grantsSpells` dichiarata sulle 3 mosse di lancio uniche del
 * catalogo DWR (`mago.lanciare-incantesimi-arcani`,
 * `chierico.lanciare-incantesimi-divini` riusata da Druido/Paladino-Sacro via
 * bundle, `warlock.lanciare-incantesimi-del-patrono`).
 *
 * **PR-A2 (FI-36 + FI-67)**: refactor SSOT che ha sostituito le pipeline
 * ad-hoc per Mago/Chierico/Druido/Paladino/Warlock (5 sub-componenti
 * `*NativeSpellList`) con un unico `<SpellPickPanel>` parametrizzato dalla
 * grant. Il pool viene calcolato da {@link getSpellPoolForGrantInContext}
 * che incapsula:
 *  - linea Divina LIDV → discriminazione Chierico/Druido/Paladino-Sacro
 *    dalla mossa-padre + Tradizione + `requiresArchetype` Elfo/Nano;
 *  - Mago/Warlock → pool diretto via `getSpellPoolForGrant`.
 *
 * Lo stato `draft.spells` viene mantenuto in sync con: spell auto-aggiunte
 * dalle grant `all-of-level`/`fixed-list` + spell scelte dall'utente nelle
 * grant `fixed`/`lte-character-level`/`any-with-cap`.
 */
export function SectionSpellSelection() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  // In builder L1 tutte le mosse sono "appena prese": passiamo l'array
  // espanso come `justAddedMoves` (e `previousMoves: []`). Le grant
  // `on-each-level-up` (es. Mago Corpus Arcano «+1 spell ogni level-up»)
  // NON sono attive al L1 — partono dal LU successivo (filtrate dal
  // service via `trigger === 'on-take'`).
  const activeGrants = useMemo<readonly ActiveSpellGrant[]>(() => {
    if (!draft.classId) return [];
    const expanded = expandStartingMoveRefsWithBundles(draft.moves);
    return getDisplayableSpellGrantsForLevelUpMany(draft.classId, [], expanded);
  }, [draft.classId, draft.moves]);

  const ctx = useMemo<SpellPoolContext>(
    () => ({
      moves: draft.moves,
      // Senza classId non ci sono grant attive (early return sopra); il valore
      // fallback è inerte e non viene effettivamente usato in quel caso.
      classId: draft.classId ?? 'guerriero',
      archetypeId: draft.archetypeId,
      characterLevel: 1,
    }),
    [draft.moves, draft.classId, draft.archetypeId],
  );

  const poolsByGrantKey = useMemo<Record<string, readonly Spell[]>>(() => {
    const map: Record<string, readonly Spell[]> = {};
    for (const grant of activeGrants) {
      map[getGrantKey(grant)] = getSpellPoolForGrantInContext(grant, ctx);
    }
    return map;
  }, [activeGrants, ctx]);

  const [selectionsByGrantKey, setSelectionsByGrantKey] = useState<Record<string, string[]>>(() =>
    rehydrateSelectionsFromDraft(activeGrants, draft.spells, poolsByGrantKey),
  );

  // **Rehydrate cross-render**: quando cambiano le mosse del PG (es.
  // cambio Tradizione, cambio archetipo, switch Ordine Sacro ↔ Sovrannaturale),
  // le grant + pools cambiano → eventuali selezioni "orfane" (spell non più
  // nel pool) vanno potate. Lo stato locale viene allineato senza distruggere
  // selezioni ancora valide.
  useEffect(() => {
    setSelectionsByGrantKey((prev) =>
      pruneSelectionsForCurrentPools(prev, activeGrants, poolsByGrantKey),
    );
  }, [activeGrants, poolsByGrantKey]);

  const lineList = useMemo<ActiveSpellList | undefined>(
    () => findChiericoLineActiveList(draft.moves),
    [draft.moves],
  );

  const computedSpells = useMemo<SpellRef[]>(() => {
    const refs: SpellRef[] = [];
    const seen = new Set<string>();
    const add = (id: string) => {
      if (seen.has(id)) return;
      seen.add(id);
      refs.push({ id });
    };
    for (const grant of activeGrants) {
      const key = getGrantKey(grant);
      const pool = poolsByGrantKey[key] ?? [];
      if (isAutoSpellGrantLevelRule(grant.grant.levelRule)) {
        for (const spell of pool) add(spell.id);
      } else {
        const picks = selectionsByGrantKey[key] ?? [];
        const poolIds = new Set(pool.map((s) => s.id));
        for (const id of picks) if (poolIds.has(id)) add(id);
      }
    }
    return refs;
  }, [activeGrants, poolsByGrantKey, selectionsByGrantKey]);

  useEffect(() => {
    dispatch({ type: 'SET_SPELLS', spells: computedSpells });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedSpells]);

  if (activeGrants.length === 0) {
    return <NoAccessPlaceholder />;
  }

  return (
    <div className="flex flex-col gap-8">
      {activeGrants.map((grant) => {
        const key = getGrantKey(grant);
        const pool = poolsByGrantKey[key] ?? [];
        return (
          <SpellPickPanel
            key={key}
            grant={grant}
            pool={pool}
            selectedIds={selectionsByGrantKey[key] ?? []}
            onChange={(next) => setSelectionsByGrantKey((s) => ({ ...s, [key]: next }))}
            headerText={describePanelHeader(grant, pool, lineList)}
            introNote={describePanelIntroNote(grant, lineList, draft.archetypeId)}
          />
        );
      })}
    </div>
  );
}

function NoAccessPlaceholder() {
  return (
    <p className="text-ink-soft text-sm italic">
      Non hai accesso ad alcun tipo di incantesimo. Acquisisci una mossa di lancio (es.{' '}
      <em>Lanciare Incantesimi Arcani</em> del Mago, <em>Lanciare Incantesimi Divini</em> del
      Chierico — più una Tradizione Sacra in <em>Divinità</em> — oppure scegli{' '}
      <em>Divinità della Natura</em> come mossa iniziale del Druido, o <em>Ordine Sacro</em> del
      Paladino con una Tradizione Sacra) per sbloccare la sezione.
    </p>
  );
}

// ============================================================================
// Header + intro note builder
// ============================================================================

/**
 * Costruisce il testo dell'header del `<SpellPickPanel>` per builder L1.
 *
 * Branching: linea Divina (LIDV) → Orazioni/Spell per classe + Tradizione;
 * Mago/Warlock → "Trucchetti/Suppliche" L0 + "Incantesimi di 1° livello".
 * Per le grant pick aggiunge il sotto-titolo «scegli N di M».
 */
function describePanelHeader(
  grant: ActiveSpellGrant,
  pool: readonly Spell[],
  lineList: ActiveSpellList | undefined,
): string {
  const isAuto = isAutoSpellGrantLevelRule(grant.grant.levelRule);
  const level = inferGrantLevel(grant);
  const prefix = poolPrefixLabel(grant, level, lineList);

  if (isAuto) return prefix;

  const count = grant.grant.count ?? 0;
  return `${prefix} (scegli ${count} di ${pool.length})`;
}

function poolPrefixLabel(
  grant: ActiveSpellGrant,
  level: number | undefined,
  lineList: ActiveSpellList | undefined,
): string {
  // Linea Divina
  if (lineList !== undefined && grant.moveId === 'chierico.lanciare-incantesimi-divini') {
    const classLabel = chiericoLineClassLabel(lineList);
    const tradLabel = chiericoLineTradizioneLabel(lineList);
    if (level === 0) return `Orazioni ${classLabel} · ${tradLabel}`;
    return `Incantesimi di 1° livello · ${tradLabel}`;
  }
  // Warlock
  if (grant.moveId === 'warlock.lanciare-incantesimi-del-patrono') {
    if (level === 0) return 'Suppliche del Warlock';
    return 'Incantesimi di 1° livello del Warlock';
  }
  // Mago LIA
  if (grant.moveId === 'mago.lanciare-incantesimi-arcani') {
    if (grant.grant.levelRule.kind === 'fixed-list') return 'Trucchetti del Mago';
    return 'Incantesimi di 1° livello del Mago';
  }
  // Fallback generico (es. Magnus / Tomo / Rivelazioni — non attivi a L1 ma
  // copertura per safety)
  return `Da «${grant.moveId}»`;
}

function chiericoLineClassLabel(list: ActiveSpellList): string {
  if (list.classId === 'chierico') return 'del Chierico';
  if (list.classId === 'druido') return 'del Druido';
  return 'del Paladino';
}

function chiericoLineTradizioneLabel(list: ActiveSpellList): string {
  if (list.classId === 'druido') return 'Tradizione della Natura';
  const trad = (list as { tradizione: string }).tradizione;
  return `Tradizione ${capitalize(trad)}`;
}

function capitalize(s: string): string {
  if (s.length === 0) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function inferGrantLevel(grant: ActiveSpellGrant): number | undefined {
  const rule = grant.grant.levelRule;
  if (rule.kind === 'fixed' || rule.kind === 'all-of-level') return rule.level;
  return undefined;
}

function describePanelIntroNote(
  grant: ActiveSpellGrant,
  lineList: ActiveSpellList | undefined,
  archetypeId: string | undefined,
): string | undefined {
  // L'intro note appare solo sul primo pannello di ciascun "trigger" (L0 auto
  // o pickable). Per evitare ripetizione su L0+L1 dello stesso pannello,
  // mostriamo la nota solo sulla grant L0 (oppure sulla grant 0-indexed).
  if (grant.grantIndex !== 0) return undefined;

  if (lineList && grant.moveId === 'chierico.lanciare-incantesimi-divini') {
    return introNoteForChiericoLine(lineList, archetypeId);
  }
  if (grant.moveId === 'warlock.lanciare-incantesimi-del-patrono') {
    return 'Il Vincolo del Tomo contiene tutte le suppliche della lista del Warlock.';
  }
  if (grant.moveId === 'mago.lanciare-incantesimi-arcani') {
    return 'Inizi il gioco con tutti i trucchetti della lista del Mago.';
  }
  return undefined;
}

function introNoteForChiericoLine(
  list: ActiveSpellList,
  archetypeId: string | undefined,
): string | undefined {
  if (list.mode === 'chierico-native') {
    return `Hai accesso a tutte le orazioni della tua Tradizione Sacra.${voceDellaTerraWarning(archetypeId)}`;
  }
  if (list.mode === 'druido-native') {
    return 'Hai scelto Divinità della Natura come mossa iniziale: la Tradizione Sacra del dio che servi è quella della Natura, e hai accesso a tutti i suoi incantesimi.';
  }
  if (list.mode === 'paladino-native') {
    const trad = list.tradizione;
    const voce =
      trad === 'negromanzia' || trad === 'valore' ? voceDellaTerraWarning(archetypeId) : '';
    const natura =
      trad === 'natura' && archetypeId !== 'elfo'
        ? ' Attenzione: la Tradizione Natura richiede archetipo Elfo.'
        : '';
    return `Hai scelto Ordine Sacro con la Tradizione Sacra ${capitalize(trad)}: hai accesso a tutti gli incantesimi della tua Tradizione.${voce}${natura}`;
  }
  return undefined;
}

function voceDellaTerraWarning(archetypeId: string | undefined): string {
  if (archetypeId === 'nano') return '';
  return " L'orazione Voce della Terra è disponibile solo se il tuo archetipo è Nano.";
}

// ============================================================================
// Rehydrate / pruning helpers
// ============================================================================

/**
 * Al primo render del componente, ricostruisce lo stato selezioni "pick" dal
 * `draft.spells` esistente — copre il refresh / ritorno allo Step Spell dopo
 * aver navigato altri step.
 *
 * Per ogni grant pick: filtra `draft.spells` mantenendo gli IDs che sono nel
 * pool della grant. Le spell di grant auto NON sono incluse (sono comunque
 * auto-aggiunte dal compute downstream).
 */
function rehydrateSelectionsFromDraft(
  grants: readonly ActiveSpellGrant[],
  draftSpells: readonly SpellRef[],
  poolsByGrantKey: Record<string, readonly Spell[]>,
): Record<string, string[]> {
  const draftIds = new Set(draftSpells.map((s) => s.id));
  const out: Record<string, string[]> = {};
  for (const grant of grants) {
    if (isAutoSpellGrantLevelRule(grant.grant.levelRule)) continue;
    const key = getGrantKey(grant);
    const pool = poolsByGrantKey[key] ?? [];
    out[key] = pool.filter((s) => draftIds.has(s.id)).map((s) => s.id);
  }
  return out;
}

/**
 * Quando il pool di una grant cambia (es. cambio Tradizione → spell diversi
 * disponibili), pota dalle selezioni le spell non più nel pool. Mantiene
 * stabile l'identità del record se nessuna modifica.
 */
function pruneSelectionsForCurrentPools(
  prev: Record<string, string[]>,
  grants: readonly ActiveSpellGrant[],
  poolsByGrantKey: Record<string, readonly Spell[]>,
): Record<string, string[]> {
  const activeKeys = new Set(grants.map(getGrantKey));
  let changed = false;
  const next: Record<string, string[]> = {};
  for (const [key, ids] of Object.entries(prev)) {
    if (!activeKeys.has(key)) {
      changed = true;
      continue;
    }
    const poolIds = new Set((poolsByGrantKey[key] ?? []).map((s) => s.id));
    const filtered = ids.filter((id) => poolIds.has(id));
    if (filtered.length !== ids.length) changed = true;
    next[key] = filtered;
  }
  // Inizializza chiavi nuove (grant appena attivata) con array vuoto.
  for (const grant of grants) {
    if (isAutoSpellGrantLevelRule(grant.grant.levelRule)) continue;
    const key = getGrantKey(grant);
    if (!(key in next)) {
      next[key] = [];
      changed = true;
    }
  }
  return changed ? next : prev;
}
